import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useContext,
} from "react";
import { toPairs, keys, values } from "lodash";
import { toast } from "react-toastify";
import {
  Button, Popover, Checkbox, Radio,
} from "antd";
import { CheckSquareOutlined, CloseOutlined, DownloadOutlined } from "@ant-design/icons";
import OutsideClickHandler from "react-outside-click-handler";
import { getUser, pause } from "../../../../utils/helpers";
import { fetchSequenceData } from "../../../../actions/chemistry";
import { ChemBioPendingContext } from "../ChemBioPendingContext";
import "./GenerateSequence.css";

const GenerateSequence = forwardRef((_, ref) => {
  const {
    showSearch,
    loading,
    selectedGenerate: parentSelectedMap,
    enableExtractedColumn,
    enableExtractedColumnSelection,
    instrumentMap,
    methodLoading,
    instrumentBrandsMap,
  } = useContext(ChemBioPendingContext);

  const [generating, setGenerating] = useState(false);
  const [selectedGenerate, setSelectedGenerate] = useState(parentSelectedMap);
  const [selectedInstrument, setSelectedInstrument] = useState(new Map());
  const [openPopover, setOpenPopover] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState("");

  // We need this useEffect for the select all checkbox on change event and
  // the toggle event for the Generate Sequence button. For these events, we need to update the state.
  useEffect(() => {
    // update the selectedGenerate only when the parentSelectedMap is valid and the enableExtractedColumn is true
    // to reduce rendering times
    if (parentSelectedMap.has("extract") && enableExtractedColumn) {
      setSelectedGenerate(parentSelectedMap);
    }
  }, [parentSelectedMap, enableExtractedColumn]);

  // This method is used in the parent component to trigger the selectedGenerate update when
  // each individual genista id checkbox on change.
  useImperativeHandle(ref, () => ({
    updateSelectedMap(map) {
      setSelectedGenerate(map);
    },
  }));

  // Every time when the popover closes, reset the checkbox map
  useEffect(() => {
    if (!openPopover) {
      setSelectedInstrument(new Map());
      setSelectedBrand("");
    }
  }, [openPopover]);

  /**
 * Exports the provided data to an Excel file and triggers a download.
 * @param {string} fileName - The name for the generated Excel file.
 * @param {Array} excelData - The data to be written into the Excel file, expected to be an array of objects.
 */
  const downloadSeqFile = (fileName, csvContent) => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

    // Create a link element
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute("download", `${fileName}.csv`);

    // Append link to the body and trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /**
   * Triggers the download of the selected generate samples' sequence files.
   * This function fetches the sequence data from the API and then downloads it
   * as an Excel file. If there is an error, it will toast the error message.
   * @returns {Promise<void>} Resolves when the download is complete.
   */
  const generateSeqFiles = async () => {
    setOpenPopover(false);
    setGenerating(true);
    const selected = [...Array.from(selectedGenerate.get("extract")).values(), ...Array.from(selectedGenerate.get("load")).values()];
    const payload = {
      user_id: getUser().id,
      samples: selected.map(([, {
        genista_id, commodity, order_id, test_id, sample_type,
      }]) => ({
        genista_id,
        commodity: commodity || "",
        order_id,
        test_id,
        sample_type,
      })),
      instrument_ids: Array.from(selectedInstrument.values()),
    };
    const apiPath = selectedBrand === "Thermo Fisher" ? "generatesequence/" : "generatesequenceagilent/";
    const { success, result, message } = await fetchSequenceData(payload, apiPath);
    if (!success) {
      toast.error(message);
      setGenerating(false);
      return;
    }

    const files = toPairs(result);
    let delayCount = 0;
    let totalFiles = 0;

    for (let i = 0; i < files.length; i++) {
      const [fileName, rawData] = files[i];

      // Replace '/' with '\' in "Instrument Method" and "Path" fields
      rawData.forEach((item) => {
        if (item["Instrument Method"]) {
          item["Instrument Method"] = item["Instrument Method"].replace(/\//g, "\\");
        }
        if (item.Path) {
          item.Path = item.Path.replace(/\//g, "\\");
        }
      });
      const headers = keys(rawData[0]).map((value) => `"${value}"`).join(","); // Create CSV headers
      const rows = rawData.map((row) => values(row)
        .map((value) => `"${value}"`) // Add quotes for safety
        .join(","));
      // The "Bracket Type=4" needs to be shown in the first cell in the first row
      const csvContent = selectedBrand === "Thermo Fisher" ? ["Bracket Type=4", headers, ...rows].join("\n") : [headers, ...rows].join("\n");
      downloadSeqFile(fileName, csvContent);
      totalFiles++;

      // Pause every 10 downloads to comply with Chrome's limit
      if (++delayCount >= 10) {
        await pause(1500); //eslint-disable-line
        delayCount = 0;
      }
    }
    if (totalFiles === 0) {
      toast.info("No data");
    } else {
      toast.success(`Downloaded ${totalFiles} Sequence files`);
    }
    setGenerating(false);
  };

  const totalSelectedCount = selectedGenerate.get("extract")?.size + selectedGenerate.get("load")?.size;

  const handleInstrumentSelect = (e, instrumentId) => {
    const checked = e.target.checked;
    setSelectedInstrument((prev) => {
      const newState = new Map(prev);
      if (checked) {
        newState.set(instrumentId, parseInt(instrumentId));
      } else {
        newState.delete(instrumentId);
      }
      return newState;
    });
  };

  const handleSelectBrand = (e) => {
    setSelectedBrand(e.target.value);
    setSelectedInstrument(new Map());
  };

  const popoverContent = () => (
    <OutsideClickHandler onOutsideClick={() => setOpenPopover(false)}>
      <div className="GSPopoverSection">
        <label className="GSPopoverSectionTitle">Select a brand</label>
        <Radio.Group
          className="GSPopoverBrandRadio"
          options={keys(instrumentBrandsMap).map((brandName) => ({ value: brandName, label: brandName }))}
          onChange={handleSelectBrand}
          value={selectedBrand}
        />
      </div>
      {selectedBrand && (
      <div className="GSPopoverSection">
        <label className="GSPopoverSectionTitle">Select instrument(s)</label>
        <div className="GenerateSequence__PopoverCheckboxContainer">
          {instrumentBrandsMap[selectedBrand].map((instrumentId, idx) => <Checkbox key={idx} checked={selectedInstrument.has(instrumentId)} onChange={(e) => handleInstrumentSelect(e, instrumentId)}>{instrumentMap.get(parseInt(instrumentId))?.instrument_name}</Checkbox>)}
        </div>
      </div>
      )}
      <div className="GenerateSequence__PopoverButton">
        <Button
          type="link"
          disabled={selectedInstrument.size === 0 || generating || loading}
          onClick={generateSeqFiles}
        >
          Download
        </Button>
      </div>
    </OutsideClickHandler>
  );

  return (
    <>
      {showSearch && (
      <div className="GenerateSequenceContainer">
        <Popover
          placement="top"
          open={openPopover}
          content={enableExtractedColumn && keys(instrumentBrandsMap).length > 1 ? popoverContent : null}
          className="GenerateSequence__Popover"
        >
          <Button
            variant="outlined"
            icon={enableExtractedColumn ? <DownloadOutlined /> : <CheckSquareOutlined />}
            disabled={generating || loading || keys(instrumentBrandsMap).length === 0 || (enableExtractedColumn && totalSelectedCount === 0)}
            onClick={enableExtractedColumn ? () => setOpenPopover(true) : () => enableExtractedColumnSelection(true)}
            loading={generating || methodLoading}
          >
            <span className="GenerateSequenceButton">
              {enableExtractedColumn ? `Download Sequence (${totalSelectedCount})` : "Generate Sequence"}
            </span>
          </Button>
        </Popover>
        {enableExtractedColumn && (
        <Button
          variant="outlined"
          disabled={generating || loading}
          onClick={() => enableExtractedColumnSelection(false)}
          icon={<CloseOutlined />}
        />
        )}
      </div>
      )}
    </>
  );
});

export default GenerateSequence;
